import { useSyncExternalStore } from 'react'
import { defaultTheme, fonts, motion, radii, themes } from '@lunary/ui-tokens'
import type { ThemeName } from '@lunary/ui-tokens'
import { themeVars } from '@lunary/ui-web/theme-vars'

const STORAGE_KEY = 'lunary.theme'
const listeners = new Set<() => void>()

function staticVars(): Record<string, string> {
  return {
    '--font-display': fonts.display,
    '--font-body': fonts.body,
    '--radius-field': `${radii.field}px`,
    '--radius-panel': `${radii.panel}px`,
    '--radius-bar': `${radii.bar}px`,
    '--radius-md': `${radii.md}px`,
    '--radius-lg': `${radii.lg}px`,
    '--radius-arch': `${radii.arch}px`,
    '--radius-arch-base': `${radii.archBase}px`,
    '--theme-fade': `${motion.themeFadeMs}ms`,
  }
}

function isTheme(value: unknown): value is ThemeName {
  return value === 'dark' || value === 'light'
}

function readChoice(): ThemeName | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return isTheme(value) ? value : null
  } catch {
    return null
  }
}

const systemQuery = (): MediaQueryList | null =>
  typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia('(prefers-color-scheme: light)') : null

const systemTheme = (): ThemeName => (systemQuery()?.matches ? 'light' : defaultTheme)

let choice: ThemeName | null = readChoice()
let current: ThemeName = choice ?? systemTheme()

function apply() {
  const root = document.documentElement
  for (const [key, value] of Object.entries({ ...staticVars(), ...themeVars(current) })) root.style.setProperty(key, value)
  root.dataset.theme = current
  root.style.colorScheme = current
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themes[current].bg)
}

function set(next: ThemeName) {
  if (next === current) return
  current = next
  apply()
  listeners.forEach((l) => l())
}

export function initTheme() {
  apply()
  systemQuery()?.addEventListener('change', () => {
    if (choice === null) set(systemTheme())
  })
}

function writeChoice(next: ThemeName) {
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    return
  }
}

export function savedThemeChoice(): ThemeName | null {
  return choice
}

export function applyRemoteTheme(next: ThemeName) {
  choice = next
  writeChoice(next)
  set(next)
}

export function setTheme(next: ThemeName) {
  choice = next
  writeChoice(next)
  set(next)
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useTheme(): ThemeName {
  return useSyncExternalStore(subscribe, () => current)
}
